<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <oxd-text
      v-if="loadFailed"
      tag="p"
      class="orangehrm-card-container expensa-error"
    >
      {{ $t('expensa.detail_error') }}
    </oxd-text>

    <div v-if="expense && editing" class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('expensa.edit_expense') }} - {{ $t(kindLabel) }}
      </oxd-text>
      <oxd-divider />
      <expense-form
        :kind="expense.kind"
        :expense="expense"
        @saved="onSaved"
        @cancel="editing = false"
      />
    </div>

    <template v-else-if="expense">
      <div class="orangehrm-card-container">
        <div class="expensa-head">
          <oxd-text tag="h6" class="orangehrm-main-title">
            {{ $t(kindLabel) }}
          </oxd-text>
          <status-pill :label="statusLabel" :tone="expense.status" />
        </div>
        <oxd-text v-if="expense.locked" class="expensa-label">
          {{ $t('expensa.locked_expense') }}
        </oxd-text>
        <oxd-divider />
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item v-for="field in fields" :key="field.label">
            <oxd-text class="expensa-label">{{ field.label }}</oxd-text>
            <a v-if="field.href" :href="field.href">{{ field.value }}</a>
            <oxd-text v-else class="expensa-value">{{ field.value }}</oxd-text>
          </oxd-grid-item>
        </oxd-grid>
        <template v-if="expense.policyViolations.length">
          <oxd-divider />
          <oxd-text class="expensa-label">{{
            $t('expensa.col_flags')
          }}</oxd-text>
          <ul class="expensa-flags">
            <li v-for="(v, i) in expense.policyViolations" :key="i">
              {{
                $t(
                  v.severity === 'block'
                    ? 'expensa.severity_block'
                    : 'expensa.severity_warn',
                )
              }}: {{ v.message }}
            </li>
          </ul>
        </template>
        <template v-if="technical">
          <oxd-divider />
          <details>
            <summary>{{ $t('expensa.technical_data') }}</summary>
            <pre class="expensa-technical">{{ technical }}</pre>
          </details>
        </template>
        <template v-if="canEdit">
          <oxd-divider />
          <div class="orangehrm-action-buttons-container expensa-actions">
            <oxd-button
              display-type="danger"
              class="orangehrm-sm-button"
              :label="$t('expensa.action_delete')"
              :disabled="busy"
              @click="onDelete"
            />
            <oxd-button
              display-type="secondary"
              class="orangehrm-sm-button"
              :label="$t('general.edit')"
              :disabled="busy"
              @click="editing = true"
            />
          </div>
        </template>
      </div>

      <div class="orangehrm-card-container expensa-section">
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{ $t('expensa.pages_section') }}
        </oxd-text>
        <oxd-text v-if="!expense.pages.length" class="expensa-label">
          {{ $t('expensa.no_pages') }}
        </oxd-text>
        <page-viewer v-else :pages="expense.pages" />
      </div>
    </template>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog.vue';
import useEmployeeNameTranslate from '@/core/util/composable/useEmployeeNameTranslate';
import ExpenseForm from '@/orangehrmExpensaPlugin/components/ExpenseForm.vue';
import PageViewer from '@/orangehrmExpensaPlugin/components/PageViewer.vue';
import StatusPill from '@/orangehrmExpensaPlugin/components/StatusPill.vue';
import {expensaApi, errorMessage, pageUrl} from '../util/api';
import {canEditExpense} from '../util/expenseRules';
import {day, eur, instant} from '../util/format';
import {expenseStatusKey, kindKey} from '../util/labels';

const dash = (value) =>
  value === null || value === undefined || value === '' ? '—' : String(value);

export default {
  name: 'ExpensaExpenseDetail',
  components: {
    'delete-confirmation': DeleteConfirmationDialog,
    'expense-form': ExpenseForm,
    'page-viewer': PageViewer,
    'status-pill': StatusPill,
  },
  props: {
    expenseId: {
      type: Number,
      required: true,
    },
  },
  setup() {
    const {$tEmpName} = useEmployeeNameTranslate();
    return {$tEmpName};
  },
  data() {
    return {
      expense: null,
      me: null,
      loadFailed: false,
      editing: false,
      busy: false,
    };
  },
  computed: {
    kindLabel() {
      return kindKey(this.expense.kind);
    },
    statusLabel() {
      return this.$t(expenseStatusKey(this.expense.status));
    },
    canEdit() {
      return canEditExpense(this.expense, this.me);
    },
    technical() {
      const raw = {
        extraction: this.expense.extraction,
        scanMetadata: this.expense.scanMetadata,
      };
      return raw.extraction || raw.scanMetadata
        ? JSON.stringify(raw, null, 2)
        : '';
    },
    fields() {
      const e = this.expense;
      const yes = (v) => this.$t(v ? 'general.yes' : 'general.no');
      const list = [
        {
          label: this.$t('expensa.owner'),
          value: dash(e.owner ? this.$tEmpName(e.owner) : null),
        },
        {label: this.$t('general.date'), value: day(e.date)},
      ];
      if (e.kind === 'RECEIPT') {
        list.push(
          {label: this.$t('expensa.col_category'), value: dash(e.categoryName)},
          {label: this.$t('expensa.merchant'), value: dash(e.merchant)},
          {
            label: this.$t('expensa.merchant_vat_number'),
            value: dash(e.merchantVatNumber),
          },
          {
            label: this.$t('general.amount'),
            value: e.amount ? `${e.amount} ${e.currency}` : '—',
          },
        );
      } else {
        list.push({
          label: this.$t('expensa.col_category'),
          value: dash(e.categoryName),
        });
      }
      list.push(
        {label: this.$t('expensa.col_amount_eur'), value: eur(e.amountEur)},
        {label: this.$t('expensa.fx_rate'), value: dash(e.fxRate)},
        {label: this.$t('expensa.fx_date'), value: day(e.fxDate)},
      );
      if (e.mileage) {
        list.push(
          {
            label: this.$t('expensa.route_from'),
            value: dash(e.mileage.routeFrom),
          },
          {label: this.$t('expensa.route_to'), value: dash(e.mileage.routeTo)},
          {
            label: this.$t('expensa.distance_km'),
            value: dash(e.mileage.distanceKm),
          },
          {
            label: this.$t('expensa.round_trip'),
            value: yes(e.mileage.roundTrip),
          },
          {
            label: this.$t('expensa.total_km'),
            value: dash(e.mileage.totalKm),
          },
          {
            label: this.$t('expensa.rate_per_km'),
            value: dash(e.mileage.ratePerKm),
          },
        );
      }
      if (e.perDiem) {
        const p = e.perDiem;
        list.push(
          {label: this.$t('expensa.trip_start'), value: instant(p.tripStart)},
          {label: this.$t('expensa.trip_end'), value: instant(p.tripEnd)},
          {label: this.$t('expensa.trip_country'), value: dash(p.country)},
          {
            label: this.$t('expensa.trip_destination'),
            value: dash(p.destination),
          },
          {
            label: this.$t('expensa.meals_provided'),
            value: yes(p.mealsProvided),
          },
          {
            label: this.$t('expensa.lodging_provided'),
            value: yes(p.lodgingProvided),
          },
          {label: this.$t('expensa.per_diem_days'), value: dash(p.days)},
          {label: this.$t('expensa.exempt_amount'), value: eur(p.exemptAmount)},
          {
            label: this.$t('expensa.taxable_amount'),
            value: eur(p.taxableAmount),
          },
        );
      }
      list.push(
        {
          label: this.$t('expensa.document_type'),
          value: e.documentType
            ? this.$t(`expensa.doc_${e.documentType.toLowerCase()}`)
            : '—',
        },
        {
          label: this.$t('expensa.vat_rate'),
          value:
            e.vatRate === null || e.vatRate === undefined
              ? '—'
              : `${e.vatRate}%`,
        },
        {label: this.$t('expensa.vat_amount'), value: eur(e.vatAmount)},
        {label: this.$t('expensa.net_amount'), value: eur(e.netAmount)},
        {
          label: this.$t('expensa.vat_deductible_amount'),
          value: eur(e.vatDeductibleAmount),
        },
        {
          label: this.$t('expensa.payment_method'),
          value: e.paymentMethod
            ? this.$t(`expensa.pay_${e.paymentMethod.toLowerCase()}`)
            : '—',
        },
        {label: this.$t('expensa.reimbursable'), value: yes(e.reimbursable)},
        {label: this.$t('expensa.project'), value: dash(e.project)},
        {
          label: this.$t('general.description'),
          value: dash(e.description),
        },
        {label: this.$t('general.notes'), value: dash(e.notes)},
        {
          label: this.$t('expensa.tags'),
          value: dash((e.tags || []).join(', ')),
        },
        {
          label: this.$t('expensa.col_report'),
          value: e.report ? e.report.title : '—',
          href: e.report ? pageUrl('viewReport', e.report.id) : null,
        },
        {
          label: this.$t('expensa.duplicate_of'),
          value: dash(e.duplicateOf),
          href: e.duplicateOf ? pageUrl('viewExpense', e.duplicateOf) : null,
        },
        {label: this.$t('expensa.quality_score'), value: dash(e.qualityScore)},
        {
          label: this.$t('expensa.quality_flags'),
          value: dash((e.qualityFlags || []).join(', ')),
        },
        {
          label: this.$t('expensa.extraction_confidence'),
          value: dash(e.extractionConfidence),
        },
        {label: this.$t('expensa.created_on'), value: instant(e.createdAt)},
        {label: this.$t('expensa.updated_on'), value: instant(e.updatedAt)},
        {label: this.$t('expensa.client_id'), value: dash(e.clientId)},
      );
      return list;
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    async load() {
      try {
        const [expense, me] = await Promise.all([
          expensaApi('/expenses').get(this.expenseId),
          expensaApi('/me')
            .getAll()
            .catch(() => null),
        ]);
        this.expense = expense.data.data;
        this.me = me ? me.data.data : null;
      } catch (e) {
        this.loadFailed = true;
        this.fail(e);
      }
    },
    fail(e) {
      this.$toast.error({
        title: this.$t('general.error'),
        message: errorMessage(e),
      });
    },
    onSaved(expense) {
      this.expense = expense;
      this.editing = false;
    },
    async onDelete() {
      const confirmation = await this.$refs.deleteDialog.showDialog();
      if (confirmation !== 'ok' || this.busy) return;
      this.busy = true;
      try {
        await expensaApi('/expenses').delete(this.expense.id);
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t('expensa.toast_expense_deleted'),
        });
        window.location.href = pageUrl('viewExpenses');
      } catch (e) {
        this.busy = false;
        this.fail(e);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-head {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.expensa-section {
  margin-top: 1rem;
}
.expensa-label {
  font-size: 0.8rem;
  opacity: 0.7;
}
.expensa-value {
  word-break: break-word;
}
.expensa-error {
  padding: 1.2rem;
}
.expensa-flags {
  margin: 0.25rem 0 0;
  padding-left: 1.2rem;
}
.expensa-technical {
  overflow: auto;
  font-size: 0.8rem;
}
</style>
